import React, { useState } from "react";
import { Link, withRouter } from "react-router-dom";
import { getLandingConfig } from "./applicationLandings";
import { submitApplication } from "./applicationsApi";
import "./Applications.css";

const EMPTY = {
  contact_name: "",
  email: "",
  phone: "",
  name: "",
  website: "",
  social_links: "",
  location: "",
  mission: "",
  description: "",
  experience: "",
  reason: "",
  notes: "",
  agreement_user: false,
  agreement_terms: false,
  agreement_privacy: false,
  agreement_understanding: false,
};

function PersonalInfoFields({ form, onChange }) {
  return (
    <fieldset className="apply-form__fieldset">
      <legend>Personal information</legend>
      <div className="apply-form__grid">
        <label className="apply-form__field">
          <span>Full name *</span>
          <input
            className="form-control"
            name="contact_name"
            value={form.contact_name}
            onChange={onChange}
            required
            autoComplete="name"
          />
        </label>
        <label className="apply-form__field">
          <span>Email *</span>
          <input
            className="form-control"
            type="email"
            name="email"
            value={form.email}
            onChange={onChange}
            required
            autoComplete="email"
          />
        </label>
        <label className="apply-form__field">
          <span>Phone</span>
          <input
            className="form-control"
            name="phone"
            value={form.phone}
            onChange={onChange}
            autoComplete="tel"
          />
        </label>
      </div>
    </fieldset>
  );
}

function EntityInfoFields({ form, onChange, entityLabel }) {
  return (
    <fieldset className="apply-form__fieldset">
      <legend>Entity information</legend>
      <div className="apply-form__grid">
        <label className="apply-form__field apply-form__field--full">
          <span>{entityLabel} *</span>
          <input
            className="form-control"
            name="name"
            value={form.name}
            onChange={onChange}
            required
          />
        </label>
        <label className="apply-form__field">
          <span>Website</span>
          <input
            className="form-control"
            name="website"
            value={form.website}
            onChange={onChange}
            placeholder="https://"
          />
        </label>
        <label className="apply-form__field">
          <span>Location</span>
          <input
            className="form-control"
            name="location"
            value={form.location}
            onChange={onChange}
          />
        </label>
        <label className="apply-form__field apply-form__field--full">
          <span>Social media links</span>
          <textarea
            className="form-control"
            name="social_links"
            value={form.social_links}
            onChange={onChange}
            rows={3}
            placeholder="One URL per line"
          />
        </label>
      </div>
    </fieldset>
  );
}

function NarrativeFields({ form, onChange }) {
  return (
    <fieldset className="apply-form__fieldset">
      <legend>About your application</legend>
      <div className="apply-form__stack">
        <label className="apply-form__field">
          <span>Mission</span>
          <textarea
            className="form-control"
            name="mission"
            value={form.mission}
            onChange={onChange}
            rows={3}
          />
        </label>
        <label className="apply-form__field">
          <span>Description</span>
          <textarea
            className="form-control"
            name="description"
            value={form.description}
            onChange={onChange}
            rows={4}
          />
        </label>
        <label className="apply-form__field">
          <span>Experience</span>
          <textarea
            className="form-control"
            name="experience"
            value={form.experience}
            onChange={onChange}
            rows={3}
          />
        </label>
        <label className="apply-form__field">
          <span>Reason for applying</span>
          <textarea
            className="form-control"
            name="reason"
            value={form.reason}
            onChange={onChange}
            rows={3}
          />
        </label>
        <label className="apply-form__field">
          <span>Additional notes</span>
          <textarea
            className="form-control"
            name="notes"
            value={form.notes}
            onChange={onChange}
            rows={3}
          />
        </label>
      </div>
    </fieldset>
  );
}

function AgreementFields({ form, onToggle }) {
  const items = [
    {
      key: "agreement_user",
      label: "I agree to the User Agreement.",
    },
    {
      key: "agreement_terms",
      label: "I agree to the Terms & Conditions.",
    },
    {
      key: "agreement_privacy",
      label: "I agree to the Privacy Policy.",
    },
    {
      key: "agreement_understanding",
      label: "I understand submission does not guarantee approval.",
    },
  ];

  return (
    <fieldset className="apply-form__fieldset">
      <legend>Agreements</legend>
      <p className="apply-landing__notice" role="note">
        Submission of an application does not guarantee approval.
      </p>
      <ul className="apply-form__agreements">
        {items.map((item) => (
          <li key={item.key}>
            <label className="apply-form__check">
              <input
                type="checkbox"
                name={item.key}
                checked={!!form[item.key]}
                onChange={onToggle}
              />
              <span>{item.label}</span>
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  );
}

/**
 * Shared public application form for Organization / Creator / Project.
 */
function ApplicationFormPage({ applicationType, history }) {
  const config = getLandingConfig(applicationType);
  const [form, setForm] = useState({ ...EMPTY });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const agreementsAccepted =
    form.agreement_user &&
    form.agreement_terms &&
    form.agreement_privacy &&
    form.agreement_understanding;

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onToggle = (event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!agreementsAccepted || submitting) {
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const body = await submitApplication({
        type: config.type,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        name: form.name,
        website: form.website,
        social_links: form.social_links,
        location: form.location,
        mission: form.mission,
        description: form.description,
        experience: form.experience,
        reason: form.reason,
        notes: form.notes,
        agreement_user: form.agreement_user ? 1 : 0,
        agreement_terms: form.agreement_terms ? 1 : 0,
        agreement_privacy: form.agreement_privacy ? 1 : 0,
        agreement_understanding: form.agreement_understanding ? 1 : 0,
      });
      if (body && body.success && body.data) {
        try {
          window.sessionStorage.setItem(
            "tpg_application_receipt",
            JSON.stringify(body.data)
          );
        } catch (e) {
          // ignore
        }
        history.push(`/apply/success?type=${encodeURIComponent(config.type)}`);
        return;
      }
      setError(
        (body && (body.error_messages || body.error)) ||
          "Unable to submit application."
      );
    } catch (e) {
      setError("Unable to submit application. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div className="community-page apply-landing">
      <header className="community-page__hero">
        <div className="community-page__shell">
          <p className="community-page__eyebrow">
            <Link to={config.path}>{config.title}</Link>
          </p>
          <h1 className="community-page__title">Application</h1>
          <p className="community-page__lede">
            Tell us about yourself and your {config.type}. Required fields are
            marked with *.
          </p>
        </div>
      </header>

      <main className="community-page__shell apply-landing__main">
        <form className="apply-form" onSubmit={onSubmit} noValidate={false}>
          <PersonalInfoFields form={form} onChange={onChange} />
          <EntityInfoFields
            form={form}
            onChange={onChange}
            entityLabel={config.entityLabel}
          />
          <NarrativeFields form={form} onChange={onChange} />
          <AgreementFields form={form} onToggle={onToggle} />

          {error ? (
            <p className="apply-form__error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="apply-form__actions">
            <button
              type="submit"
              className="btn btn-danger"
              disabled={!agreementsAccepted || submitting}
            >
              {submitting ? "Submitting…" : "Submit application"}
            </button>
            <Link className="btn btn-outline-light" to={config.path}>
              Back
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}

export default withRouter(ApplicationFormPage);

export const OrganizationApplyPage = withRouter((props) => (
  <ApplicationFormPage {...props} applicationType="organization" />
));

export const CreatorApplyPage = withRouter((props) => (
  <ApplicationFormPage {...props} applicationType="creator" />
));

export const ProjectApplyPage = withRouter((props) => (
  <ApplicationFormPage {...props} applicationType="project" />
));
